/**
 * 时间区间工具函数
 * 用于航食产品时间适配功能
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// 加载dayjs插件
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 时间区间数据结构
 * @typedef {Object} TimeRange
 * @property {string} start - 开始时间，格式: HH:mm (24小时制)
 * @property {string} end - 结束时间，格式: HH:mm (24小时制)
 */

/**
 * 验证时间格式是否正确
 * @param {string} timeStr - 时间字符串，格式: HH:mm
 * @returns {boolean} 是否为有效的时间格式
 */
export const isValidTimeFormat = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") {
        return false;
    }

    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(timeStr);
};

/**
 * 将时间字符串转换为分钟数（从00:00开始计算）
 * @param {string} timeStr - 时间字符串，格式: HH:mm
 * @returns {number} 分钟数
 */
export const timeToMinutes = (timeStr) => {
    if (!isValidTimeFormat(timeStr)) {
        throw new Error(`无效的时间格式: ${timeStr}`);
    }

    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes;
};

/**
 * 将分钟数转换为时间字符串
 * @param {number} minutes - 分钟数
 * @returns {string} 时间字符串，格式: HH:mm
 */
export const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
};

/**
 * 从航班信息中获取机场时区
 * @param {Object} flight - 航班对象
 * @param {Object} airportsMap - 机场数据映射 (可选)
 * @returns {string} 机场时区标识符，默认返回UTC
 */
export const getAirportTimezoneFromFlight = (flight, airportsMap = {}) => {
    if (!flight) {
        return "UTC";
    }

    // 优先使用始发机场的时区（因为航食产品通常在始发机场配置）
    const airportCode = flight.departure_airport;

    if (!airportCode) {
        console.warn("航班缺少始发机场信息:", flight);
        return "UTC";
    }

    // 从机场映射中获取时区信息
    if (airportsMap && airportsMap[airportCode] && airportsMap[airportCode].timezone) {
        return airportsMap[airportCode].timezone;
    }

    // 如果没有机场映射，尝试从全局缓存获取
    if (window.cachedAirports && Array.isArray(window.cachedAirports)) {
        const airport = window.cachedAirports.find((a) => a.iata_code === airportCode);
        if (airport && airport.timezone) {
            return airport.timezone;
        }
    }

    console.warn(`未找到机场 ${airportCode} 的时区信息，使用UTC作为默认值`);
    return "UTC";
};

/**
 * 将时间范围从指定时区转换为UTC时间范围
 * @param {TimeRange} timeRange - 时间范围对象
 * @param {string} sourceTimezone - 源时区
 * @returns {TimeRange} UTC时间范围对象
 */
export const convertTimeRangeToUTC = (timeRange, sourceTimezone) => {
    if (!timeRange || !timeRange.start || !timeRange.end) {
        return timeRange;
    }

    if (sourceTimezone === "UTC") {
        return timeRange; // 已经是UTC，无需转换
    }

    try {
        // 使用今天的日期创建完整的日期时间
        const today = dayjs().format("YYYY-MM-DD");

        // 创建源时区的开始时间
        const startDateTime = `${today}T${timeRange.start}:00`;
        const startLocal = dayjs.tz(startDateTime, sourceTimezone);
        const startUTC = startLocal.utc();

        // 创建源时区的结束时间
        const endDateTime = `${today}T${timeRange.end}:00`;
        let endLocal = dayjs.tz(endDateTime, sourceTimezone);

        // 处理跨天情况：如果结束时间小于开始时间，说明跨天了
        const startMinutes = timeToMinutes(timeRange.start);
        const endMinutes = timeToMinutes(timeRange.end);
        if (endMinutes < startMinutes) {
            endLocal = endLocal.add(1, "day");
        }

        const endUTC = endLocal.utc();

        return {
            start: startUTC.format("HH:mm"),
            end: endUTC.format("HH:mm"),
        };
    } catch (error) {
        console.error("时间范围时区转换错误:", error, { timeRange, sourceTimezone });
        return timeRange; // 转换失败时返回原始时间范围
    }
};

/**
 * 检查时间是否在指定的时间区间内
 * @param {string} timeStr - 要检查的时间，格式: HH:mm 或 ISO日期字符串
 * @param {TimeRange} timeRange - 时间区间
 * @param {string} targetTimezone - 目标时区（用于时间匹配）
 * @returns {boolean} 是否在时间区间内
 */
export const isTimeInRange = (timeStr, timeRange, targetTimezone = "UTC") => {
    if (!timeRange || !timeRange.start || !timeRange.end) {
        return true; // 没有时间限制则始终匹配
    }

    // 如果是ISO日期字符串，需要转换为目标时区的时间
    let checkTime = timeStr;
    if (timeStr.includes("T") || timeStr.includes(" ")) {
        try {
            // 将UTC时间转换为目标时区的时间
            let utcTime = timeStr;
            if (typeof timeStr === "string" && timeStr.includes("T") && !timeStr.endsWith("Z") && !timeStr.includes("+") && !timeStr.includes("-")) {
                // 对于不带Z也不带时区偏移的ISO格式，添加Z确保识别为UTC
                utcTime = timeStr + "Z";
            }

            // 转换为目标时区
            const localTime = targetTimezone === "UTC" ? dayjs.utc(utcTime) : dayjs.utc(utcTime).tz(targetTimezone);

            if (!localTime.isValid()) {
                console.warn("无效的日期时间格式:", timeStr);
                return false;
            }

            checkTime = `${localTime.hour().toString().padStart(2, "0")}:${localTime.minute().toString().padStart(2, "0")}`;
        } catch (error) {
            console.warn("时区转换错误:", error, { timeStr, targetTimezone });
            // 降级处理：使用原始逻辑
            const date = new Date(timeStr);
            if (isNaN(date.getTime())) {
                console.warn("无效的日期时间格式:", timeStr);
                return false;
            }
            checkTime = `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
        }
    }

    if (!isValidTimeFormat(checkTime)) {
        console.warn("无效的时间格式:", checkTime);
        return false;
    }

    const checkMinutes = timeToMinutes(checkTime);
    const startMinutes = timeToMinutes(timeRange.start);
    const endMinutes = timeToMinutes(timeRange.end);

    // 处理跨天的时间区间（如 23:00-02:00）
    if (startMinutes <= endMinutes) {
        // 正常时间区间（如 08:00-12:00）
        return checkMinutes >= startMinutes && checkMinutes <= endMinutes;
    } else {
        // 跨天时间区间（如 23:00-02:00）
        return checkMinutes >= startMinutes || checkMinutes <= endMinutes;
    }
};

/**
 * 检查时间是否在任意一个时间区间内
 * @param {string} timeStr - 要检查的时间
 * @param {TimeRange[]} timeRanges - 时间区间列表
 * @param {string} targetTimezone - 目标时区（用于时间匹配）
 * @returns {boolean} 是否在任意一个时间区间内
 */
export const isTimeInAnyRange = (timeStr, timeRanges, targetTimezone = "UTC") => {
    if (!timeRanges || timeRanges.length === 0) {
        return true; // 没有时间限制则始终匹配
    }

    return timeRanges.some((range) => isTimeInRange(timeStr, range, targetTimezone));
};

/**
 * 检查航班时间是否匹配产品的时间区间
 *
 * 重要说明：
 * - 航班时间（std/etd）：UTC格式的完整日期时间
 * - 产品时间范围（applicable_time_ranges）：机场本地时间的HH:mm格式
 * - 匹配逻辑：将产品时间范围转换为UTC，然后与航班UTC时间比较
 *
 * @param {Object} flight - 航班对象
 * @param {Object} product - 产品对象
 * @param {string} targetTimezone - 目标时区（已废弃，保留为兼容性参数）
 * @param {Object} airportsMap - 机场数据映射（可选）
 * @returns {boolean} 是否匹配
 */
export const isFlightTimeMatchProduct = (flight, product, targetTimezone = "UTC", airportsMap = {}) => {
    if (!flight || !product) {
        return false;
    }

    // 获取航班时间（优先使用ETD，其次STD）
    const flightTime = flight.etd || flight.std || flight.departure_time;
    if (!flightTime) {
        console.warn("航班缺少起飞时间信息:", flight);
        return false;
    }

    // 获取产品的时间区间
    const timeRanges = product.applicable_time_ranges;
    if (!timeRanges || timeRanges.length === 0) {
        return true; // 没有时间限制则始终匹配
    }

    // 获取机场时区
    const airportTimezone = getAirportTimezoneFromFlight(flight, airportsMap);

    console.log(`🕐 时间匹配检查 - 航班: ${flight.id}, 机场: ${flight.departure_airport}, 时区: ${airportTimezone}`);

    // 将产品的时间范围从机场本地时间转换为UTC时间范围
    const utcTimeRanges = timeRanges.map((range) => {
        const utcRange = convertTimeRangeToUTC(range, airportTimezone);
        console.log(`🔄 时间范围转换: ${range.start}-${range.end} (${airportTimezone}) -> ${utcRange.start}-${utcRange.end} (UTC)`);
        return utcRange;
    });

    // 使用UTC时间进行匹配
    const isMatch = isTimeInAnyRange(flightTime, utcTimeRanges, "UTC");

    console.log(`✅ 匹配结果: 航班时间 ${flightTime} ${isMatch ? "匹配" : "不匹配"} 产品时间范围`);

    return isMatch;
};

/**
 * 格式化时间区间为显示文本
 * @param {TimeRange[]} timeRanges - 时间区间列表
 * @returns {string} 格式化后的文本
 */
export const formatTimeRanges = (timeRanges) => {
    if (!timeRanges || timeRanges.length === 0) {
        return "全天";
    }

    return timeRanges.map((range) => `${range.start}-${range.end}`).join(", ");
};

/**
 * 验证时间区间的有效性
 * @param {TimeRange} timeRange - 时间区间
 * @returns {Object} 验证结果 {valid: boolean, error?: string}
 */
export const validateTimeRange = (timeRange) => {
    if (!timeRange) {
        return { valid: false, error: "时间区间不能为空" };
    }

    if (!timeRange.start || !timeRange.end) {
        return { valid: false, error: "开始时间和结束时间都不能为空" };
    }

    if (!isValidTimeFormat(timeRange.start)) {
        return { valid: false, error: "开始时间格式不正确，应为 HH:mm" };
    }

    if (!isValidTimeFormat(timeRange.end)) {
        return { valid: false, error: "结束时间格式不正确，应为 HH:mm" };
    }

    return { valid: true };
};

/**
 * 验证时间区间列表的有效性
 * @param {TimeRange[]} timeRanges - 时间区间列表
 * @returns {Object} 验证结果 {valid: boolean, error?: string}
 */
export const validateTimeRanges = (timeRanges) => {
    if (!timeRanges) {
        return { valid: true }; // null 是有效的（表示无时间限制）
    }

    if (!Array.isArray(timeRanges)) {
        return { valid: false, error: "时间区间必须是数组格式" };
    }

    for (let i = 0; i < timeRanges.length; i++) {
        const result = validateTimeRange(timeRanges[i]);
        if (!result.valid) {
            return { valid: false, error: `第${i + 1}个时间区间：${result.error}` };
        }
    }

    return { valid: true };
};
